import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/stores/auth-store";
import { useAuthUserProfileStore } from "@/lib/stores/profile-store";
import { useUserInitialization } from "@/lib/hooks/use-user-initialization";
import { useQueryClient } from "@tanstack/react-query";
import { userInitializationKeys } from "@/lib/hooks/use-user-initialization";
import Logger from "@/lib/logger";

export function EnhancedAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { setUser, setLoading } = useAuthStore();
  const { setProfile } = useAuthUserProfileStore();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Get current user from auth store
  const { user } = useAuthStore();

  // Use our new initialization hook for the authenticated user
  const {
    data: initData,
    isLoading: initLoading,
    error: initError,
  } = useUserInitialization(user?.id || null);

  useEffect(() => {
    // Set loading to true while we check the session
    setLoading(true);

    // Get initial session
    const initializeAuth = async () => {
      try {
        // Get the session first
        const {
          data: { session },
        } = await supabase.auth.getSession();

        // Update the auth store with user data (or null if not authenticated)
        setUser(session?.user ?? null);

        // For logged-out users, immediately set loading to false
        if (!session?.user) {
          setLoading(false);
        }

        // Log auth status (for debugging only)
        Logger.info(
          `Enhanced Auth initialized. User ${
            session?.user ? "authenticated" : "not authenticated"
          }`
        );
      } catch (error) {
        Logger.error("Error initializing enhanced auth:", error);
        setUser(null);
        setLoading(false); // Always set loading to false on error
      }
    };

    // Initialize auth
    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      // Update the user in the store
      setUser(session?.user ?? null);

      // For logged-out users, immediately set loading to false
      if (!session?.user) {
        setLoading(false);
      }

      // Clear initialization cache when user changes
      if (session?.user) {
        queryClient.removeQueries({
          queryKey: userInitializationKeys.user(session.user.id),
        });
      } else {
        // Clear all user initialization data when logging out
        queryClient.removeQueries({
          queryKey: userInitializationKeys.all,
        });
        setProfile(null);
      }

      Logger.info(
        `Enhanced Auth state changed. User ${
          session?.user ? "authenticated" : "not authenticated"
        }`
      );
    });

    // Clean up on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [setUser, setLoading, location, queryClient, setProfile]);

  // Update profile store when initialization data loads
  useEffect(() => {
    if (initData?.profile) {
      setProfile(initData.profile);
      Logger.info("Profile data loaded from enhanced auth initialization", {
        username: initData.profile.username,
        onboardingCompleted: initData.onboarding_completed,
        isPremium: initData.premium_status.isPremium,
        worksCount: initData.works_count,
      });
    }
  }, [initData, setProfile]);

  // Set loading to false when either:
  // 1. No user is authenticated (auth loading complete)
  // 2. User is authenticated and initialization data has loaded
  useEffect(() => {
    if (!user) {
      // No user, auth loading is complete
      setLoading(false);
    } else if (user && !initLoading) {
      // User exists and initialization is complete (success or error)
      setLoading(false);

      if (initError) {
        Logger.error("Failed to load user initialization data:", initError);
        // Continue anyway - the app can still function with individual queries as fallback
      }
    }
  }, [user, initLoading, initError, setLoading]);

  return <>{children}</>;
}
